import { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import sdk from "@farcaster/miniapp-sdk";
import { notificationsApi } from "./api/notifications";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import { Navbar } from "./components/navbar";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { IOSInstallPrompt } from "./components/IOSInstallPrompt";
import { AndroidInstallPrompt } from "./components/AndroidInstallPrompt";
import { DesktopBlocker } from "./components/DesktopBlocker";
import { SplashScreen } from "./components/SplashScreen";
import { Home } from "./pages/Home";
import { Challenges } from "./pages/Challenges";
import { Challenge } from "./pages/Challenge";
import { CreateChallenge } from "./pages/CreateChallenge";
import { ChallengeSuccess } from "./pages/ChallengeSuccess";
import { ChallengeJoinSuccess } from "./pages/ChallengeJoinSuccess";
import { ChallengePreview } from "./pages/ChallengePreview";
import { MyChallenges } from "./pages/MyChallenges";
import { ChallengeDetail } from "./pages/ChallengeDetail";
import { Profile } from "./pages/Profile";
import ProfileCreation from "./pages/ProfileCreation";
import { ProfileSuccess } from "./pages/ProfileSuccess";
import { WelcomeRunner } from "./pages/WelcomeRunner";
import { AnalysisSummary } from "./pages/AnalysisSummary";
import { AnalysisReport } from "./pages/AnalysisReport";

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <SplashScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show splash screen while auth is initializing
  if (isLoading) {
    return <SplashScreen />;
  }

  // Hide navbar on onboarding flow pages, success screens, and detail views with fixed CTAs
  const shouldShowNavbar =
    isAuthenticated &&
    location.pathname !== "/profile-creation" &&
    location.pathname !== "/profile-success" &&
    location.pathname !== "/welcome-runner" &&
    location.pathname !== "/analysis-summary" &&
    location.pathname !== "/analysis-report" &&
    location.pathname !== "/challenge-success" &&
    location.pathname !== "/challenge-join-success" &&
    location.pathname !== "/create-challenge" &&
    !location.pathname.startsWith("/challenge-preview") &&
    !location.pathname.startsWith("/my-challenge/");

  return (
    <>
      {shouldShowNavbar && <Navbar />}
      <UpdatePrompt />
      <IOSInstallPrompt />
      <AndroidInstallPrompt />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/challenges"
          element={
            <ProtectedRoute>
              <Challenges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenge/:id"
          element={
            <ProtectedRoute>
              <Challenge />
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenge-preview/:id"
          element={
            <ProtectedRoute>
              <ChallengePreview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-challenge"
          element={
            <ProtectedRoute>
              <CreateChallenge />
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenge-success"
          element={
            <ProtectedRoute>
              <ChallengeSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenge-join-success"
          element={
            <ProtectedRoute>
              <ChallengeJoinSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile-creation"
          element={
            <ProtectedRoute>
              <ProfileCreation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile-success"
          element={
            <ProtectedRoute>
              <ProfileSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/welcome-runner"
          element={
            <ProtectedRoute>
              <WelcomeRunner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analysis-summary"
          element={
            <ProtectedRoute>
              <AnalysisSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analysis-report"
          element={
            <ProtectedRoute>
              <AnalysisReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-challenges"
          element={
            <ProtectedRoute>
              <MyChallenges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-challenge/:id"
          element={
            <ProtectedRoute>
              <ChallengeDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  const farcasterInitialized = useRef(false);

  // Signal to Farcaster that the app is ready (only when running inside Farcaster client)
  useEffect(() => {
    if (farcasterInitialized.current) return;
    farcasterInitialized.current = true;

    const initFarcaster = async () => {
      try {
        const context = await sdk.context;
        if (!context) return;

        sdk.actions.ready(); // Signal app is ready

        // Prompt to add miniapp if not already added
        if (!context.client?.added) {
          await sdk.actions.addFrame();
          // Send welcome notification after successful add
          const fid = context.user?.fid;
          if (fid) {
            notificationsApi.sendWelcome(fid);
          }
        }
      } catch {
        // Not in Farcaster - expected for web/mobile usage
      }
    };

    initFarcaster();
  }, []);

  return (
    <ToastProvider>
      <DesktopBlocker />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Analytics />
      <SpeedInsights />
    </ToastProvider>
  );
}

export default App;
